import React, { useEffect, useState } from "react";
import { PENCIL, PROFILE } from "../../assets";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { selectUser, setUser } from "../../states/slices/authReducer";
import ButtonV2 from "../../shared/components/buttonV2";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { ProfileUpdateTypes } from "../../types";
import { deleteAccount, profileUpdate } from "../../Services/auth";
import { toast } from "react-hot-toast";
import DeleteModal from "../../shared/components/deleteModal";
import { useNavigate } from "react-router-dom";
import ModalV2 from "../../shared/components/modalV2";

function GlobalSettings() {
  const user = useAppSelector(selectUser);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowComfirmPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formField, setFormField] = useState({
    name: "",
    dob: "",
    currentPassword: "",
    newpassword: "",
    confirmNewpassword: "",
  });

  const handleinputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormField((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getPasswordPayloads = () => {
    if (formField.newpassword && formField.confirmNewpassword) {
      return {
        current_password: formField.currentPassword,
        password: formField.newpassword,
        password_confirmation: formField.confirmNewpassword,
      };
    } else return null;
  };

  const handleSaveChanges = async () => {
    setSaving(true);

    const passwordpayloads = getPasswordPayloads();
    const payload = {
      ...passwordpayloads,
      name: formField.name,
      dob: formField.dob,
    };

    try {
      const response = await profileUpdate(payload);
      if (response) {
        toast.success(response?.message);
        dispatch(setUser(response?.data));
        if (
          formField.currentPassword &&
          formField.newpassword &&
          formField.confirmNewpassword
        ) {
          setFormField({
            ...formField,
            confirmNewpassword: "",
            newpassword: "",
            currentPassword: "",
          });
        }
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (user) {
      setFormField({ ...formField, name: user?.name, dob: user?.dob });
    }
  }, []);

  const handleDeleteAccount = async () => {
    setLoading(true);
    try {
      const response = await deleteAccount(user?.id);
      if (response) {
        setDeleteModal(false);
        navigate("/");
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-3 sm:p-5">
      <div className="flex items-start flex-col">
        <span className="text-[#2A2E34] font-semibold text-xl sm:text-2xl">
          Account Settings
        </span>
        <span className="text-primary-50 font-medium text-sm">
          Manage your personal information and preferences
        </span>
      </div>

      <div className="w-full flex flex-col lg:flex-row items-start gap-5 mt-4">
        <div className="w-full lg:w-[350px] items-center justify-center flex flex-col gap-y-2 h-[256px] border border-[#E9EAEB] rounded-xl flex-shrink-0">
          <img src={PROFILE} className="w-[100px] h-[100px]" alt="" />
          <div className="flex flex-col gap-y-1 items-center">
            <span className="text-lg font-semibold text-customDark text-center">
              {user?.name}
            </span>
            <span className="text-xs font-medium text-primary-50 text-center break-all">
              {user?.email}
            </span>
          </div>
          <div className="text-xs font-medium bg-[#F3FAF3] text-[#00A63E] w-fit h-fit px-1.5 py-1">
            Active
          </div>
        </div>

        <div className="flex flex-col items-start gap-y-3 w-full min-w-0">
          <div className="w-full border border-[#E9EAEB] rounded-xl">
            <div className="w-full py-3 px-2 bg-lightGold rounded-t-xl text-[16px] font-semibold text-[#2A2E34]">
              Account Info
            </div>
            <div className="w-full flex flex-col sm:flex-row items-center gap-4 p-4">
              <div className="flex flex-col gap-y-2 w-full">
                <span className="text-[#4F5762] font-medium text-sm">
                  Username
                </span>
                <div className="flex items-center gap-x-2 border border-borderColor rounded-lg p-2">
                  <input
                    type="text"
                    name="name"
                    value={formField.name}
                    onChange={handleinputChange}
                    className="w-full outline-none bg-transparent min-w-0"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-y-2 w-full">
                <span className="text-[#4F5762] font-medium text-sm">
                  Date of Birth
                </span>
                <div className="flex items-center gap-x-2 border border-borderColor rounded-lg p-2">
                  <input
                    type="date"
                    name="dob"
                    defaultValue={formField.dob}
                    onChange={handleinputChange}
                    className="w-full outline-none bg-transparent min-w-0"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="w-full border border-[#E9EAEB] rounded-xl">
            <div className="w-full py-3 px-2 bg-lightGold rounded-t-xl text-[16px] font-semibold text-[#2A2E34]">
              Password Management
            </div>
            <div className="w-full grid grid-cols-1 lg:grid-cols-2 items-center gap-4 p-4">
              <div className="flex flex-col gap-y-2 w-full lg:col-span-2">
                <span className="text-[#4F5762] font-medium text-sm">
                  Current Password
                </span>
                <div className="flex items-center gap-x-2 border border-borderColor rounded-lg p-2">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    placeholder="******************"
                    name="currentPassword"
                    value={formField.currentPassword}
                    onChange={handleinputChange}
                    className="w-full outline-none bg-transparent min-w-0"
                  />
                  <span
                    className="cursor-pointer text-gray-500 flex-shrink-0"
                    onClick={() => setShowCurrentPassword((prev) => !prev)}
                  >
                    {showCurrentPassword ? (
                      <FaEyeSlash size={16} />
                    ) : (
                      <FaEye size={16} />
                    )}
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-y-2 w-full">
                <span className="text-[#4F5762] font-medium text-sm">
                  New Password
                </span>
                <div className="flex items-center gap-x-2 border border-borderColor rounded-lg p-2">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    placeholder="******************"
                    name="newpassword"
                    value={formField.newpassword}
                    onChange={handleinputChange}
                    className="w-full outline-none bg-transparent min-w-0"
                  />
                  <span
                    className="cursor-pointer text-gray-500 flex-shrink-0"
                    onClick={() => setShowNewPassword((prev) => !prev)}
                  >
                    {showNewPassword ? (
                      <FaEyeSlash size={16} />
                    ) : (
                      <FaEye size={16} />
                    )}
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-y-2 w-full">
                <span className="text-[#4F5762] font-medium text-sm">
                  Confirm Password
                </span>
                <div className="flex items-center gap-x-2 border border-borderColor rounded-lg p-2">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="******************"
                    name="confirmNewpassword"
                    value={formField.confirmNewpassword}
                    onChange={handleinputChange}
                    className="w-full outline-none bg-transparent min-w-0"
                  />
                  <span
                    className="cursor-pointer text-gray-500 flex-shrink-0"
                    onClick={() => setShowComfirmPassword((prev) => !prev)}
                  >
                    {showConfirmPassword ? (
                      <FaEyeSlash size={16} />
                    ) : (
                      <FaEye size={16} />
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-3">
            <ButtonV2
              title={"Delete Account"}
              btnStyle={
                "bg-[#E7000B1A] w-full sm:w-fit h-fit px-3 py-2 rounded-lg"
              }
              textStyle={"text-sm font-medium text-[#E7000B]"}
              handleClick={() => setDeleteModal(true)}
            />
            <ButtonV2
              title={"Save Changes"}
              loading={saving}
              loaderColor="#ffffff"
              btnStyle={
                "bg-globalGold w-full sm:w-fit h-fit px-3 py-2 rounded-lg"
              }
              textStyle={"text-sm font-medium text-white"}
              handleClick={handleSaveChanges}
            />
          </div>
        </div>
      </div>

      <ModalV2
        isOpen={deleteModal}
        isClose={() => setDeleteModal(false)}
        maxWidth={"w-[90vw] max-w-[400px]"}
        edges={"rounded-xl"}
      >
        <div className="">
          <div className="border-b text-lg font-semibold text-customDark px-5 py-3 flex items-start">
            Delete Account?
          </div>

          <div className="flex flex-col gap-y-2 items-center justify-center p-5">
            <span className="text-lg font-medium text-[#2A2E34] text-center">
              Are you sure you want to delete your account?
            </span>
            <span className="text-[16px] font-medium text-[#5D7284] text-center">
              This account will be permanently removed from your access and
              cannot be recovered.
            </span>
          </div>

          <div className="flex w-full items-center gap-x-3 p-5">
            <ButtonV2
              title={"Cancel"}
              btnStyle={"w-full border border-borderColor rounded-xl p-3"}
              textStyle={"text-sm font-medium text-customDark"}
              handleClick={() => setDeleteModal(false)}
            />
            <ButtonV2
              title={"Yes, Delete"}
              btnStyle={
                "w-full bg-[#FF3B2D] flex items-center justify-center rounded-xl p-3"
              }
              loaderColor="#ffffff"
              loading={loading}
              textStyle={"text-sm font-medium text-white"}
              handleClick={handleDeleteAccount}
            />
          </div>
        </div>
      </ModalV2>
    </div>
  );
}

export default GlobalSettings;
